import {
  ComponentSize,
  ComponentVariant,
  ComponentAppearance,
} from 'src/modules/shared/types/component.type';
import { TextSize } from 'src/modules/shared/components/atoms/text/text.styles';

/**
 * Base classes applied to all empty state components
 */
export const emptyStateBaseClasses: string[] = [
  'w-full',
  'h-full',
  'rounded-lg',
  'transition-all',
  'duration-200',
];

/**
 * Size-based classes for empty state containers
 */
export const emptyStateSizeClasses: Record<ComponentSize, string> = {
  '2xs': 'p-2 space-y-2',
  xs: 'p-3 space-y-2',
  sm: 'p-4 space-y-3',
  md: 'p-6 space-y-4',
  lg: 'p-8 space-y-6',
};

/**
 * Icon container size classes
 */
export const iconContainerSizeClasses: Record<ComponentSize, string> = {
  '2xs': 'w-5 h-5',
  xs: 'w-6 h-6',
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
};

/**
 * Icon size classes for the actual icon content
 */
export const iconSizeClasses: Record<ComponentSize, string> = {
  '2xs': 'w-2.5 h-2.5',
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

/**
 * Gap classes for horizontal placement
 */
export const horizontalGapClasses: Record<ComponentSize, string> = {
  '2xs': 'gap-2',
  xs: 'gap-2',
  sm: 'gap-3',
  md: 'gap-4',
  lg: 'gap-6',
};

/**
 * Vertical spacing classes for vertical placement
 */
export const verticalSpaceClasses: Record<ComponentSize, string> = {
  '2xs': 'space-y-1',
  xs: 'space-y-1',
  sm: 'space-y-2',
  md: 'space-y-3',
  lg: 'space-y-4',
};

/**
 * Text content spacing classes
 */
export const textSpaceClasses: Record<ComponentSize, string> = {
  '2xs': 'space-y-0.5',
  xs: 'space-y-0.5',
  sm: 'space-y-1',
  md: 'space-y-1',
  lg: 'space-y-2',
};

/**
 * Filled appearance styles for each variant
 */
export const emptyStateFilledClasses: Record<ComponentVariant, string> = {
  primary: 'bg-primary-50 border border-primary-100',
  secondary: 'bg-secondary-50 border border-secondary-100',
  neutral: 'bg-neutral-50 border border-neutral-100',
  danger: 'bg-danger-50 border border-danger-100',
  warning: 'bg-warning-50 border border-warning-100',
  success: 'bg-success-50 border border-success-100',
  info: 'bg-info-50 border border-info-100',
  whatsapp: 'bg-whatsapp-50 border border-whatsapp-100',
  call: 'bg-call-50 border border-call-100',
  email: 'bg-email-50 border border-email-100',
};

/**
 * Outline appearance styles for each variant
 */
export const emptyStateOutlineClasses: Record<ComponentVariant, string> = {
  primary: 'bg-white border-2 border-primary-200',
  secondary: 'bg-white border-2 border-secondary-200',
  neutral: 'bg-white border-2 border-neutral-200',
  danger: 'bg-white border-2 border-danger-200',
  warning: 'bg-white border-2 border-warning-200',
  success: 'bg-white border-2 border-success-200',
  info: 'bg-white border-2 border-info-200',
  whatsapp: 'bg-white border-2 border-whatsapp-200',
  call: 'bg-white border-2 border-call-200',
  email: 'bg-white border-2 border-email-200',
};

/**
 * Soft appearance styles for each variant
 */
export const emptyStateSoftClasses: Record<ComponentVariant, string> = {
  primary: 'bg-primary-25 border border-primary-50',
  secondary: 'bg-secondary-25 border border-secondary-50',
  neutral: 'bg-neutral-25 border border-neutral-50',
  danger: 'bg-danger-25 border border-danger-50',
  warning: 'bg-warning-25 border border-warning-50',
  success: 'bg-success-25 border border-success-50',
  info: 'bg-info-25 border border-info-50',
  whatsapp: 'bg-whatsapp-25 border border-whatsapp-50',
  call: 'bg-call-25 border border-call-50',
  email: 'bg-email-25 border border-email-50',
};

/**
 * Ghost appearance styles for each variant
 */
export const emptyStateGhostClasses: Record<ComponentVariant, string> = {
  primary: 'bg-transparent border-0',
  secondary: 'bg-transparent border-0',
  neutral: 'bg-transparent border-0',
  danger: 'bg-transparent border-0',
  warning: 'bg-transparent border-0',
  success: 'bg-transparent border-0',
  info: 'bg-transparent border-0',
  whatsapp: 'bg-transparent border-0',
  call: 'bg-transparent border-0',
  email: 'bg-transparent border-0',
};

/**
 * Icon container background styles for each variant and appearance combination
 */
export const iconContainerClasses: Record<
  ComponentAppearance,
  Record<ComponentVariant, string>
> = {
  filled: {
    primary: 'bg-primary-100 text-primary-600',
    secondary: 'bg-secondary-100 text-secondary-600',
    neutral: 'bg-neutral-100 text-neutral-600',
    danger: 'bg-danger-100 text-danger-600',
    warning: 'bg-warning-100 text-warning-600',
    success: 'bg-success-100 text-success-600',
    info: 'bg-info-100 text-info-600',
    whatsapp: 'bg-whatsapp-100 text-whatsapp-600',
    call: 'bg-call-100 text-call-600',
    email: 'bg-email-100 text-email-600',
  },
  outline: {
    primary: 'bg-white border border-primary-300 text-primary-600',
    secondary: 'bg-white border border-secondary-300 text-secondary-600',
    neutral: 'bg-white border border-neutral-300 text-neutral-600',
    danger: 'bg-white border border-danger-300 text-danger-600',
    warning: 'bg-white border border-warning-300 text-warning-600',
    success: 'bg-white border border-success-300 text-success-600',
    info: 'bg-white border border-info-300 text-info-600',
    whatsapp: 'bg-white border border-whatsapp-300 text-whatsapp-600',
    call: 'bg-white border border-call-300 text-call-600',
    email: 'bg-white border border-email-300 text-email-600',
  },
  soft: {
    primary: 'bg-primary-50 text-primary-600',
    secondary: 'bg-secondary-50 text-secondary-600',
    neutral: 'bg-neutral-50 text-neutral-600',
    danger: 'bg-danger-50 text-danger-600',
    warning: 'bg-warning-50 text-warning-600',
    success: 'bg-success-50 text-success-600',
    info: 'bg-info-50 text-info-600',
    whatsapp: 'bg-whatsapp-50 text-whatsapp-600',
    call: 'bg-call-50 text-call-600',
    email: 'bg-email-50 text-email-600',
  },
  ghost: {
    primary: 'bg-primary-100 text-primary-600',
    secondary: 'bg-secondary-100 text-secondary-600',
    neutral: 'bg-neutral-100 text-neutral-600',
    danger: 'bg-danger-100 text-danger-600',
    warning: 'bg-warning-100 text-warning-600',
    success: 'bg-success-100 text-success-600',
    info: 'bg-info-100 text-info-600',
    whatsapp: 'bg-whatsapp-100 text-whatsapp-600',
    call: 'bg-call-100 text-call-600',
    email: 'bg-email-100 text-email-600',
  },
};

/**
 * Maps empty state size to appropriate title text size
 */
export const getTitleTextSize = (size: ComponentSize): TextSize => {
  const sizeMap: Record<ComponentSize, TextSize> = {
    '2xs': 'tiny',
    xs: 'tiny',
    sm: 'small',
    md: 'paragraph',
    lg: 'h6',
  };
  return sizeMap[size];
};

/**
 * Maps empty state size to appropriate description text size
 */
export const getDescriptionTextSize = (size: ComponentSize): TextSize => {
  const sizeMap: Record<ComponentSize, TextSize> = {
    '2xs': 'tiny',
    xs: 'tiny',
    sm: 'tiny',
    md: 'small',
    lg: 'small',
  };
  return sizeMap[size];
};
